import React, { FC, ReactElement, useEffect, useState, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { BASE_URL } from "../../constants/urlConstants";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux-toolkit/user/user-slice";
import {
    Alert, Button, Card, Col, Divider, Form,
    Input, Row, Space, Tabs, Typography,
} from "antd";
import {
    LockOutlined, LoginOutlined, MailOutlined,
    MobileOutlined, SafetyOutlined, ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { FORGOT } from "../../constants/routeConstants";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 120;

const Login: FC = (): ReactElement => {
    const history = useHistory();
    const dispatch = useDispatch();

    // ── Email/password state ──
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError, setEmailError] = useState("");

    // ── OTP state ──
    const [phone, setPhone] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [otpSuccess, setOtpSuccess] = useState("");
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // ── Resend timer state ──
    const [resendTimer, setResendTimer] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (otpSent) {
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
            startResendTimer();
        }
    }, [otpSent]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    const startResendTimer = () => {
        setResendTimer(RESEND_COOLDOWN);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current!);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // ── Helpers ──
    const redirectAfterLogin = (role: string) => {
        role === "ADMIN" ? history.push("/admin") : history.push("/");
    };

    // ── Email login ──
    const onClickSignIn = async (values: { email: string; password: string }) => {
        setEmailLoading(true);
        setEmailError("");
        try {
            const { data } = await axios.post(`${BASE_URL}/api/v1/auth/login`, values);
            localStorage.setItem("token", data.token);
            dispatch(setUser(data.user));
            redirectAfterLogin(data.role);
        } catch (err: any) {
            setEmailError(err.response?.data?.message || "Login failed.");
        } finally {
            setEmailLoading(false);
        }
    };

    // ── OTP Step 1: Send (also used for resend) ──
    const sendOtp = async (phoneNumber: string) => {
        setOtpLoading(true);
        setOtpError("");
        try {
            await axios.post(`${BASE_URL}/api/otp/send`, { phone: phoneNumber });
            setPhone(phoneNumber);
            setOtpSent(true);
            setOtp(Array(OTP_LENGTH).fill(""));
            setOtpSuccess(`OTP sent to +91 ${phoneNumber}`);
            startResendTimer();
        } catch (err: any) {
            setOtpError(err.response?.data?.message || "Failed to send OTP.");
        } finally {
            setOtpLoading(false);
        }
    };

    const onSendOtp = (values: { phone: string }) => sendOtp(values.phone);

    const onResendOtp = () => {
        if (resendTimer > 0) return;
        setOtpSuccess("");
        sendOtp(phone);
    };

    // ── OTP Step 2: Verify ──
    const onVerifyOtp = async () => {
        const otpValue = otp.join("");
        if (otpValue.length !== OTP_LENGTH) return;
        setOtpLoading(true);
        setOtpError("");
        try {
            const { data } = await axios.post(`${BASE_URL}/api/otp/verify`, { phone, otp: otpValue });
            localStorage.setItem("token", data.token);
            dispatch(setUser(data.user));
            redirectAfterLogin(data.role);
        } catch (err: any) {
            setOtpError(err.response?.data?.message || "Invalid OTP.");
            setOtp(Array(OTP_LENGTH).fill(""));
            inputRefs.current[0]?.focus();
        } finally {
            setOtpLoading(false);
        }
    };

    // ── OTP box handlers ──
    const handleOtpChange = (value: string, index: number) => {
        if (!/^\d*$/.test(value)) return;
        const updated = [...otp];
        updated[index] = value.slice(-1);
        setOtp(updated);
        if (value && index < OTP_LENGTH - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (pasted.length === OTP_LENGTH) {
            setOtp(pasted.split(""));
            inputRefs.current[OTP_LENGTH - 1]?.focus();
        }
    };

    const resetOtp = () => {
        setOtpSent(false);
        setOtp(Array(OTP_LENGTH).fill(""));
        setOtpError("");
        setOtpSuccess("");
        setPhone("");
        setResendTimer(0);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    // ── Styles ──
    const otpBoxStyle: React.CSSProperties = {
        width: 48, height: 56, textAlign: "center",
        fontSize: 22, fontWeight: 600, borderRadius: 8,
        border: "1.5px solid #d9d9d9", outline: "none",
        transition: "border-color 0.2s",
    };

    // ── Resend button label ──
    const resendLabel = resendTimer > 0
        ? `Resend OTP in ${resendTimer}s`
        : "Resend OTP";

    return (
        <Row justify="center" align="middle" style={{ minHeight: "90vh", backgroundColor: "#f5f7fa", padding: "40px 0" }}>
            <Col xs={22} sm={16} md={12} lg={8}>
                <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}>

                    <Typography.Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
                        <LoginOutlined /> Sign In
                    </Typography.Title>

                    <Tabs
                        defaultActiveKey="otp"  // ← OTP is now first/default
                        centered
                        destroyInactiveTabPane
                        onChange={() => { setEmailError(""); resetOtp(); }}
                        items={[
                            // ── TAB 1: OTP ──
                            {
                                key: "otp",
                                label: <span><MobileOutlined /> OTP</span>,
                                children: (
                                    <>
                                        {otpError && <Alert message={otpError} type="error" showIcon style={{ marginBottom: 16 }} />}
                                        {otpSuccess && !otpError && <Alert message={otpSuccess} type="success" showIcon style={{ marginBottom: 16 }} />}

                                        {!otpSent ? (
                                            <Form layout="vertical" name="otp-send-form" onFinish={onSendOtp} requiredMark={false}>
                                                <Form.Item label="Mobile Number" name="phone" rules={[
                                                    { required: true, message: "Please enter your mobile number!" },
                                                    { pattern: /^\d{10}$/, message: "Enter a valid 10-digit number" },
                                                ]}>
                                                    <Input
                                                        prefix={<><MobileOutlined />&nbsp;+91</>}
                                                        placeholder="10-digit mobile number"
                                                        size="large"
                                                        maxLength={10}
                                                    />
                                                </Form.Item>
                                                <Form.Item>
                                                    <Button type="primary" htmlType="submit" icon={<SafetyOutlined />}
                                                        size="large" loading={otpLoading} block>
                                                        Send OTP
                                                    </Button>
                                                </Form.Item>
                                            </Form>
                                        ) : (
                                            <div style={{ textAlign: "center" }}>
                                                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
                                                    Enter the OTP sent to <strong>+91 {phone}</strong>
                                                </Typography.Text>

                                                <Space size={8} style={{ marginBottom: 24 }} onPaste={handleOtpPaste}>
                                                    {otp.map((digit, i) => (
                                                        <input
                                                            key={i}
                                                            ref={(el) => (inputRefs.current[i] = el)}
                                                            value={digit}
                                                            onChange={(e) => handleOtpChange(e.target.value, i)}
                                                            onKeyDown={(e) => handleOtpKeyDown(e, i)}
                                                            maxLength={1}
                                                            inputMode="numeric"
                                                            style={{
                                                                ...otpBoxStyle,
                                                                borderColor: digit ? "#1677ff" : "#d9d9d9",
                                                            }}
                                                            onFocus={(e) => (e.target.style.borderColor = "#1677ff")}
                                                            onBlur={(e) => (e.target.style.borderColor = digit ? "#1677ff" : "#d9d9d9")}
                                                        />
                                                    ))}
                                                </Space>

                                                <Button
                                                    type="primary" icon={<LoginOutlined />} size="large"
                                                    loading={otpLoading}
                                                    disabled={otp.join("").length !== OTP_LENGTH}
                                                    onClick={onVerifyOtp} block
                                                >
                                                    Verify & Sign In
                                                </Button>

                                                {/* ── Resend OTP button ── */}
                                                <Button
                                                    type="link"
                                                    icon={<ReloadOutlined />}
                                                    disabled={resendTimer > 0 || otpLoading}
                                                    onClick={onResendOtp}
                                                    style={{ marginTop: 12 }}
                                                >
                                                    {resendLabel}
                                                </Button>

                                                <br />
                                                <Button type="link" style={{ marginTop: 4 }} onClick={resetOtp}>
                                                    ← Change number
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                ),
                            },

                            // ── TAB 2: Email/Password ──
                            {
                                key: "email",
                                label: <span><MailOutlined /> Password</span>,
                                children: (
                                    <>
                                        {emailError && <Alert message={emailError} type="error" showIcon style={{ marginBottom: 16 }} />}
                                        <Form layout="vertical" name="login-form" onFinish={onClickSignIn} requiredMark={false}>
                                            <Form.Item label="E-mail" name="email"
                                                rules={[{ required: true, message: "Please input your email!" }]}>
                                                <Input prefix={<MailOutlined />} placeholder="Enter your email" size="large" />
                                            </Form.Item>

                                            <Form.Item label="Password" name="password"
                                                rules={[{ required: true, message: "Please input your password!" }]}>
                                                <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" size="large" />
                                            </Form.Item>

                                            <Form.Item>
                                                <Space style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                                    <Button type="primary" htmlType="submit" icon={<LoginOutlined />}
                                                        size="large" loading={emailLoading}>
                                                        Sign In
                                                    </Button>
                                                    <Link to={FORGOT}>Forgot password?</Link>
                                                </Space>
                                            </Form.Item>
                                        </Form>
                                    </>
                                ),
                            },
                        ]}
                    />

                    <Divider />
                    <Typography.Paragraph style={{ textAlign: "center", marginBottom: 0 }}>
                        Don't have an account? <Link to="/registration">Sign up</Link>
                    </Typography.Paragraph>
                </Card>
            </Col>
        </Row>
    );
};

export default Login;